import { CalendarType, DayOfWeek, RoundingType } from '@lmt/shared-types';
import { SettingsSeparatorLine } from '@lmt/unicorn-shared/app/components/common/ui-kit/configuration/SettingsSeparatorLine';
import { SettingsTip } from '@lmt/unicorn-shared/app/components/common/ui-kit/configuration/SettingsTip';
import {
  CalendarConfigurationFormComponentProps,
  CalendarTypeDictionary,
  ResourcesProps,
  TipsProps,
} from '@lmt/unicorn-shared/app/components/calendar/configuration/common/types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { TimePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/TimePicker';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { RadioButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/RadioButton';
import { ToggleButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/ToggleButton';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { requiredRule } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';

import styles from './CalendarGridSettings.module.scss';
import { MultiSelectDropDownInput } from '../../../common/ui-kit/inputs/dropdown/MultiSelectDropDownInput';
import { getEnumValuesDict } from '../../../patients/list/common/types';

const calendarTypesKeys = Object.keys(CalendarType);
const millsInHour = 3600000;

export const CalendarGridSettings: React.FC<
  CalendarConfigurationFormComponentProps & TipsProps & ResourcesProps
> = (props) => {
  const i18nCalendar = useScopedTranslation('calendar_configuration');

  const { register, control, watch, setValue, formState } = props.formMethods;

  const [
    isResourceViewCalendar,
    roundingType,
    hasFilterByPlaces,
    hasFilterBySpecialConditions,
    hasFilterBySubtenants,
    hasFilterByDoctors,
    startOfDayInSecondsSinceMidnight,
    endOfDayInSecondsSinceMidnight,
    calendarType,
  ] = watch([
    'isResourceViewCalendar',
    'roundingType',
    'hasFilterByPlaces',
    'hasFilterBySpecialConditions',
    'hasFilterBySubtenants',
    'hasFilterByDoctors',
    'startOfDayInSecondsSinceMidnight',
    'endOfDayInSecondsSinceMidnight',
    'calendarType',
  ]);

  const calendarTypes: CalendarTypeDictionary[] = useMemo(
    () =>
      calendarTypesKeys
        .filter((key) => !Number.isNaN(Number(key)))
        .map((key) => ({
          key: Number(key),
          value: i18nCalendar.t(
            `calendar_types.${CalendarType[Number(key)]}` as any,
          ),
        })),
    [calendarTypesKeys],
  );

  const translatedWeekDays = useMemo(
    () => getEnumValuesDict(DayOfWeek, 'week_day'),
    [],
  );

  useEffect(
    // end time cannot be less than start time and interval should be at least one hour
    function setCorrectEndTime() {
      const startOfDay = Number(startOfDayInSecondsSinceMidnight);
      const endOfDay = Number(endOfDayInSecondsSinceMidnight);
      const secondsInHour = millsInHour / 1000;
      if (
        !isNaN(startOfDay) &&
        !isNaN(endOfDay) &&
        endOfDay - startOfDay < secondsInHour
      ) {
        setValue('endOfDayInSecondsSinceMidnight', startOfDay + secondsInHour);
      }
    },
    [startOfDayInSecondsSinceMidnight, endOfDayInSecondsSinceMidnight],
  );

  useEffect(
    // we cannot show resources in month view
    // or we disable resource view if all toggles for resource types are disabled
    function disableResourceView() {
      if (
        calendarType === CalendarType.Month ||
        (!hasFilterByPlaces &&
          !hasFilterBySpecialConditions &&
          !hasFilterByDoctors &&
          !hasFilterBySubtenants)
      ) {
        setValue('isResourceViewCalendar', false);
      }
    },
    [
      calendarType,
      hasFilterByPlaces,
      hasFilterBySpecialConditions,
      hasFilterByDoctors,
      hasFilterBySubtenants,
    ],
  );

  useEffect(
    function enablePlaceResourcesByDefault() {
      if (
        isResourceViewCalendar &&
        !hasFilterByPlaces &&
        !hasFilterBySpecialConditions &&
        !hasFilterByDoctors &&
        !hasFilterBySubtenants
      ) {
        setValue('hasFilterByPlaces', true);
      }
    },
    [isResourceViewCalendar],
  );

  useEffect(
    function enablePlaceResourcesByDefault() {
      if (!hasFilterByPlaces) {
        setValue('isShowingRoomOccupancy', false);
        setValue('isShowingSelectedRoomOccupancy', false);
      }
    },
    [hasFilterByPlaces],
  );

  const setRounding = (value: RoundingType) => {
    setValue('roundingType', value);
  };

  return (
    <>
      <div className={styles.horizontalContainer}>
        <Field
          title={i18nCalendar.t('grid.fields.calendar_type')}
          className={styles.dropdown}
        >
          <Controller
            control={control}
            name={'calendarType'}
            render={({ field: { onChange, value } }) => (
              <div className={styles.input}>
                <DropDownInput
                  useIdFunctionAsValue={true}
                  options={calendarTypes}
                  value={value}
                  idFunction={'key'}
                  getOptionLabel={'value'}
                  required={true}
                  onValueChanged={(value) => {
                    onChange(value?.key);
                  }}
                  errorText={formState.errors.calendarType?.message}
                />
              </div>
            )}
          />
        </Field>
        {calendarType === CalendarType.Week && (
          <Field
            title={i18nCalendar.t('grid.fields.week_days')}
            className={styles.dropdown}
          >
            <Controller
              control={control}
              name={'weekDays'}
              render={({ field: { onChange, value } }) => (
                <div className={styles.input}>
                  <MultiSelectDropDownInput
                    options={translatedWeekDays}
                    idFunction={'value'}
                    getOptionLabel={'label'}
                    placeholder={i18nCalendar.t('grid.fields.select_week_days')}
                    allSelectedLabel={i18nCalendar.t(
                      'grid.fields.all_week_days',
                    )}
                    onValueChanged={(newValues) => {
                      onChange(newValues.map((ev) => ev.value));
                    }}
                    values={value}
                    headerTitle={i18nCalendar.t('grid.fields.week_days')}
                  />
                </div>
              )}
            />
          </Field>
        )}
      </div>
      <div className={styles.tipText}>
        <span>{i18nCalendar.t('grid.descriptions.calendar_type')}</span>
      </div>
      <SettingsSeparatorLine />
      <Field title={''}>
        <ToggleButton
          {...register('isResourceViewCalendar')}
          disabled={calendarType === CalendarType.Month}
          title={i18nCalendar.t('grid.fields.resource_view')}
          onClick={(e) => {
            if (!e.currentTarget.checked) {
              setRounding(RoundingType.None);
            }
          }}
        />
      </Field>
      {isResourceViewCalendar ? (
        <Field
          title={i18nCalendar.t('grid.fields.rounding')}
          className={styles.field}
        >
          <div className={styles.rounding}>
            <div>
              <RadioButton
                name={'rounding_off'}
                onChange={() => setRounding(RoundingType.None)}
                checked={roundingType === RoundingType.None}
                title={i18nCalendar.t('grid.rounding.without_rounding')}
              />
              <div className={styles.roundingTip}>
                <div className={styles.durationLineNoRounding} />
              </div>
              <span className={styles.tipText}>
                {i18nCalendar.t('grid.descriptions.rounding_off')}
              </span>
            </div>
            <div>
              <RadioButton
                name={'rounding_to_12_hours'}
                onChange={() => setRounding(RoundingType.RoundingTo12Hours)}
                checked={roundingType === RoundingType.RoundingTo12Hours}
                title={i18nCalendar.t('grid.rounding.rounding_12_hours')}
              />
              <div className={styles.roundingTipWithLine}>
                <div className={styles.durationLineRounding} />
              </div>
              <span className={styles.tipText}>
                {i18nCalendar.t('grid.descriptions.rounding_12_hours')}
              </span>
            </div>
          </div>
          <div className={styles.rounding}>
            <div>
              <RadioButton
                name={'rounding_to_24_hours'}
                onChange={() => setRounding(RoundingType.RoundingTo24Hours)}
                checked={roundingType === RoundingType.RoundingTo24Hours}
                title={i18nCalendar.t('grid.rounding.rounding_24_hours')}
              />
              <div className={styles.roundingTip}>
                <div className={styles.durationLineRounding24Hours} />
              </div>
              <span className={styles.tipText}>
                {i18nCalendar.t('grid.descriptions.rounding_24_hours')}
              </span>
            </div>
          </div>
          {props.isShowingTips && (
            <SettingsTip
              text={
                roundingType !== RoundingType.None
                  ? i18nCalendar.t('tips.rounding_on')
                  : i18nCalendar.t('tips.rounding_off')
              }
              showTip={props.showTip}
              hideTip={props.hideTip}
            />
          )}
          <div>
            <Field title={''}>
              <CheckBox
                {...register('isMultiDayVisitRounding')}
                title={i18nCalendar.t('grid.rounding.multi_day_rounding')}
              />
            </Field>
          </div>
        </Field>
      ) : (
        calendarType !== CalendarType.Month && (
          <>
            <Field title={''}>
              <div className={styles.timePickersContainer}>
                <Controller
                  control={control}
                  rules={requiredRule()}
                  name={'startOfDayInSecondsSinceMidnight'}
                  render={({ field: { onChange, value } }) => (
                    <TimePicker
                      errorText={
                        formState.errors.startOfDayInSecondsSinceMidnight
                          ?.message
                      }
                      timeInMills={
                        value === undefined || value === null
                          ? undefined
                          : value * 1000
                      }
                      onTimeChanged={(value) =>
                        onChange(value ? value / 1000 : value)
                      }
                      timeEntriesIntervalInMinutes={60}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={'endOfDayInSecondsSinceMidnight'}
                  render={({ field: { onChange, value } }) => (
                    <TimePicker
                      timeInMills={
                        value === undefined || value === null
                          ? undefined
                          : value * 1000
                      }
                      onTimeChanged={(value) =>
                        onChange(value ? value / 1000 : value)
                      }
                      minTimeInMills={
                        !isNaN(Number(startOfDayInSecondsSinceMidnight))
                          ? Number(startOfDayInSecondsSinceMidnight) * 1000 +
                            millsInHour
                          : undefined
                      }
                      timeEntriesIntervalInMinutes={60}
                    />
                  )}
                />
              </div>
              <div className={styles.tipText}>
                <span>{i18nCalendar.t('grid.descriptions.time_interval')}</span>
              </div>
            </Field>
          </>
        )
      )}
    </>
  );
};
